import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area,
} from "recharts";
import { api } from "../api/client";
import { useProjects } from "../context/ProjectContext";
import { BrandMark, BRAND } from "../components/Brand";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft, FolderOpen, ChartPie, Users, Warning, Buildings, Flag,
  Stack, TrendUp,
} from "@phosphor-icons/react";

// DigiTrans palette — cycled across categorical slices/bars.
const PALETTE = [
  "#387EE6", // blue
  "#22C55E", // green
  "#F97316", // orange
  "#0EA5E9", // sky
  "#8B5CF6", // violet accent
  "#14B8A6", // teal
  "#EAB308", // amber
  "#EF4444", // red
];

// Fixed colors for known statuses (helps orient the eye across sessions).
const STATUS_COLOR = {
  "Completed": "#22C55E",
  "In Progress": "#387EE6",
  "Not Started": "#94A3B8",
  "On Hold": "#F59E0B",
  "Blocked": "#EF4444",
  "Unknown": "#CBD5E1",
};

function pctLabel(value, total) {
  if (!total) return "";
  return `${Math.round((value / total) * 100)}%`;
}

function ChartCard({ icon: Icon, title, subtitle, children, empty }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-6 shadow-sm flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="h-9 w-9 rounded-lg flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${BRAND.colors.blue}, ${BRAND.colors.green})` }}
          >
            <Icon size={18} weight="bold" className="text-white" />
          </div>
          <div>
            <div className="text-sm font-black tracking-tight" style={{ color: BRAND.colors.ink, fontFamily: "Chivo, sans-serif" }}>
              {title}
            </div>
            {subtitle ? (
              <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{subtitle}</div>
            ) : null}
          </div>
        </div>
      </div>
      <div className="flex-1 min-h-[340px]" data-testid={`chart-${title.toLowerCase().replace(/\s+/g, "-")}`}>
        {empty ? (
          <div className="h-full flex items-center justify-center text-sm text-neutral-400">
            No data yet
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, hint, tone = "blue" }) {
  const toneStyle = {
    blue: { border: BRAND.colors.blue, text: BRAND.colors.blue },
    green: { border: BRAND.colors.green, text: BRAND.colors.green },
    orange: { border: BRAND.colors.orange, text: BRAND.colors.orange },
    ink: { border: BRAND.colors.ink, text: BRAND.colors.ink },
  }[tone];
  return (
    <div
      className="bg-white border rounded-2xl p-5 shadow-sm"
      style={{ borderColor: "#E5E7EB", borderTop: `4px solid ${toneStyle.border}` }}
    >
      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 font-mono">{label}</div>
      <div
        className="mt-2 text-3xl font-black tracking-tight"
        style={{ color: toneStyle.text, fontFamily: "Chivo, sans-serif" }}
      >
        {value}
      </div>
      {hint ? <div className="mt-1 text-[11px] text-neutral-500">{hint}</div> : null}
    </div>
  );
}

export default function Analytics() {
  const { projects, projectId, setProjectId, activeProjects, project, customer } = useProjects();
  const [scope, setScope] = useState("project"); // "project" | "portfolio"
  const [stats, setStats] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(false);

  const effectiveProjectId = scope === "project" ? projectId : null;
  const canRun = scope === "portfolio" || !!projectId;

  useEffect(() => {
    let cancelled = false;
    if (!canRun) { setStats(null); setTrend(null); return; }
    setLoading(true);
    const params = scope === "project" ? { project_id: projectId } : {};
    Promise.all([
      api.get("/tasks/stats/summary", { params }),
      api.get("/analytics/completion-trend", { params: { ...params, days: 30 } }),
    ])
      .then(([s, t]) => {
        if (cancelled) return;
        setStats(s.data);
        setTrend(t.data);
      })
      .catch(() => {
        if (cancelled) return;
        setStats(null);
        setTrend(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectId, scope, canRun]);

  // Status donut data
  const statusData = useMemo(() => {
    const src = stats?.by_overall_status || {};
    return Object.entries(src)
      .map(([name, value]) => ({ name, value }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [stats]);

  const totalTasks = stats?.total || 0;

  // Team workload — combine technical_resource + functional_owner buckets,
  // pick top N assignees by count. Empty/Unknown filtered out.
  const workloadData = useMemo(() => {
    const merged = {};
    const push = (src) => {
      Object.entries(src || {}).forEach(([name, count]) => {
        if (!name || name === "Unknown" || name === "-" || name.trim() === "") return;
        merged[name] = (merged[name] || 0) + count;
      });
    };
    push(stats?.by_technical_resource);
    // Only add functional_owner if it isn't duplicating the same key
    // (usually different names, so simple additive)
    push(stats?.by_functional_owner);
    return Object.entries(merged)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [stats]);

  // Module bar
  const moduleData = useMemo(() => {
    const src = stats?.by_module || {};
    return Object.entries(src)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 12);
  }, [stats]);

  // Priority donut
  const priorityData = useMemo(() => {
    const src = stats?.by_priority || {};
    // Canonical order first, then anything else appended.
    const order = ["Critical", "High", "Medium", "Low"];
    const seen = new Set();
    const arr = [];
    order.forEach((k) => {
      if (src[k]) { arr.push({ name: k, value: src[k] }); seen.add(k); }
    });
    Object.entries(src).forEach(([k, v]) => {
      if (!seen.has(k) && v > 0) arr.push({ name: k, value: v });
    });
    return arr;
  }, [stats]);

  // Dev-status stacked bar — module × development_status
  const devStackData = useMemo(() => {
    const matrix = stats?.by_module_dev_status || {};
    const modules = Object.keys(matrix).sort();
    // Determine union of dev-status keys across modules
    const keySet = new Set();
    modules.forEach((m) => Object.keys(matrix[m] || {}).forEach((k) => keySet.add(k)));
    const keys = Array.from(keySet);
    const rows = modules.map((m) => {
      const row = { module: m };
      keys.forEach((k) => { row[k] = matrix[m]?.[k] || 0; });
      return row;
    });
    return { rows, keys };
  }, [stats]);

  // Burndown / completion trend — cumulative completions over the window
  const trendData = useMemo(() => {
    const buckets = trend?.buckets || [];
    return buckets.map((b) => ({
      date: b.date,
      shortDate: b.date.slice(5), // MM-DD
      completed: b.completed,
      cumulative: b.cumulative,
    }));
  }, [trend]);

  const completedCount = stats?.by_overall_status?.["Completed"] || 0;
  // "Not Started" bucket is treated as the not-yet-picked-up count.
  const notStartedCount = stats?.by_overall_status?.["Not Started"] || 0;
  // Anything that signals trouble — statuses vary across trackers, so we
  // sum every label that looks risky.
  const AT_RISK_KEYS = ["At Risk", "Blocked", "Critical", "On Hold", "Delayed"];
  const atRiskCount = AT_RISK_KEYS.reduce(
    (acc, k) => acc + (stats?.by_overall_status?.[k] || 0), 0,
  );
  // Everything else that is actively moving.
  const inFlightCount = Math.max(
    0,
    totalTasks - completedCount - notStartedCount - atRiskCount,
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="analytics-page">
      {/* Header */}
      <header className="h-16 border-b border-[#E5E5E5] bg-white flex items-center px-6 justify-between shrink-0">
        <div className="flex items-center gap-3">
          <BrandMark />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
              PROJECT TRACKER
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Analytics</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Scope toggle: single project vs. portfolio-wide */}
          <div className="hidden md:inline-flex items-center h-9 rounded-md border border-neutral-200 p-0.5 bg-neutral-50">
            <button
              type="button"
              onClick={() => setScope("project")}
              data-testid="analytics-scope-project"
              className={`h-8 px-3 rounded text-xs font-semibold transition-colors ${
                scope === "project"
                  ? "bg-white text-[#0F172A] shadow-sm"
                  : "text-neutral-500 hover:text-neutral-900"
              }`}
            >
              Project
            </button>
            <button
              type="button"
              onClick={() => setScope("portfolio")}
              data-testid="analytics-scope-portfolio"
              className={`h-8 px-3 rounded text-xs font-semibold transition-colors ${
                scope === "portfolio"
                  ? "text-white shadow-sm"
                  : "text-neutral-500 hover:text-neutral-900"
              }`}
              style={scope === "portfolio" ? {
                background: `linear-gradient(135deg, ${BRAND.colors.blue}, ${BRAND.colors.green})`,
              } : {}}
            >
              All Projects
            </button>
          </div>
          {scope === "project" && activeProjects.length > 0 ? (
            <div className="hidden md:flex items-center gap-2">
              <FolderOpen size={14} weight="bold" className="text-neutral-500" />
              <Select value={projectId || ""} onValueChange={setProjectId}>
                <SelectTrigger data-testid="analytics-project-selector" className="h-9 min-w-[200px]">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  {activeProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <Link to="/">
            <Button variant="outline" size="sm" data-testid="analytics-back-btn" className="h-9">
              <ArrowLeft size={14} weight="bold" className="mr-1.5" /> Dashboard
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* Title */}
        <div>
          <div
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] font-mono px-2 py-1 rounded"
            style={{ background: `linear-gradient(135deg, ${BRAND.colors.blue}, ${BRAND.colors.green})`, color: "white" }}
          >
            {scope === "portfolio" ? "Portfolio Analytics" : "Live Analytics"}
          </div>
          <h1
            className="mt-3 text-4xl font-black tracking-tight"
            style={{ fontFamily: "Chivo, sans-serif", color: BRAND.colors.ink }}
          >
            {scope === "portfolio"
              ? "All Projects"
              : (project ? project.name : "Analytics")}
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            {scope === "portfolio"
              ? `${stats?.project_count || 0} project${(stats?.project_count || 0) === 1 ? "" : "s"} · ${totalTasks} task${totalTasks === 1 ? "" : "s"} tracked`
              : `${customer ? `${customer.name} · ` : ""}${totalTasks} task${totalTasks === 1 ? "" : "s"} tracked`}
          </p>
        </div>

        {/* Loading placeholder */}
        {loading && !stats ? (
          <div className="text-sm text-neutral-500">Loading analytics…</div>
        ) : null}

        {scope === "project" && !projectId ? (
          <div className="bg-white border border-neutral-200 rounded-2xl p-10 text-center">
            <FolderOpen size={40} weight="thin" className="mx-auto text-neutral-300 mb-3" />
            <div className="text-sm text-neutral-600">Select a project above to see its analytics.</div>
          </div>
        ) : (
          <>
            {/* Top KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total Tasks" value={totalTasks} tone="ink" />
              <StatCard
                label="Completed"
                value={completedCount}
                hint={pctLabel(completedCount, totalTasks)}
                tone="green"
              />
              <StatCard
                label="In Flight"
                value={inFlightCount}
                hint={pctLabel(inFlightCount, totalTasks)}
                tone="blue"
              />
              <StatCard
                label="At Risk"
                value={atRiskCount}
                hint={pctLabel(atRiskCount, totalTasks)}
                tone="orange"
              />
            </div>

            {/* Charts grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChartCard
                icon={ChartPie}
                title="Status Distribution"
                subtitle="By overall status"
                empty={statusData.length === 0}
              >
                <ResponsiveContainer width="100%" height={340}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={70}
                      outerRadius={120}
                      paddingAngle={2}
                      label={({ value }) => value}
                    >
                      {statusData.map((entry, idx) => (
                        <Cell
                          key={entry.name}
                          fill={STATUS_COLOR[entry.name] || PALETTE[idx % PALETTE.length]}
                          stroke="#fff"
                          strokeWidth={2}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [`${value} (${pctLabel(value, totalTasks)})`, name]}
                      contentStyle={{
                        border: "1px solid #E5E7EB",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    />
                    <Legend
                      iconType="circle"
                      wrapperStyle={{ fontSize: 12, fontFamily: "IBM Plex Sans, sans-serif" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard
                icon={Users}
                title="Team Workload"
                subtitle="Top 10 assignees by task count"
                empty={workloadData.length === 0}
              >
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart
                    data={workloadData}
                    layout="vertical"
                    margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                  >
                    <defs>
                      <linearGradient id="barFill" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor={BRAND.colors.blue} />
                        <stop offset="60%" stopColor={BRAND.colors.blue} />
                        <stop offset="100%" stopColor={BRAND.colors.green} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid horizontal={false} stroke="#F1F5F9" />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: "#64748B" }}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={130}
                      tick={{ fontSize: 11, fill: "#334155" }}
                      interval={0}
                    />
                    <Tooltip
                      formatter={(v) => [`${v} task${v === 1 ? "" : "s"}`, "Load"]}
                      cursor={{ fill: "rgba(56,126,230,0.06)" }}
                      contentStyle={{
                        border: "1px solid #E5E7EB",
                        borderRadius: 8,
                        fontSize: 12,
                      }}
                    />
                    <Bar dataKey="count" fill="url(#barFill)" radius={[0, 6, 6, 0]} barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Module breakdown */}
              <ChartCard
                icon={Buildings}
                title="Tasks by Module"
                subtitle="Volume per work stream"
                empty={moduleData.length === 0}
              >
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart
                    data={moduleData}
                    margin={{ top: 10, right: 20, left: 0, bottom: 30 }}
                  >
                    <defs>
                      <linearGradient id="modBarFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={BRAND.colors.green} />
                        <stop offset="100%" stopColor={BRAND.colors.blue} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke="#F1F5F9" />
                    <XAxis
                      dataKey="name"
                      tick={{ fontSize: 11, fill: "#334155" }}
                      angle={-25}
                      textAnchor="end"
                      interval={0}
                      height={50}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: "#64748B" }}
                    />
                    <Tooltip
                      formatter={(v) => [`${v} task${v === 1 ? "" : "s"}`, "Count"]}
                      cursor={{ fill: "rgba(34,197,94,0.06)" }}
                      contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }}
                    />
                    <Bar dataKey="value" fill="url(#modBarFill)" radius={[6, 6, 0, 0]} barSize={28} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Priority split */}
              <ChartCard
                icon={Flag}
                title="Priority Split"
                subtitle="Critical / High / Medium / Low"
                empty={priorityData.length === 0}
              >
                <ResponsiveContainer width="100%" height={340}>
                  <PieChart>
                    <Pie
                      data={priorityData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={110}
                      paddingAngle={2}
                      label={({ value }) => value}
                    >
                      {priorityData.map((entry, idx) => {
                        const priorityColors = {
                          Critical: "#EF4444",
                          High: BRAND.colors.orange,
                          Medium: BRAND.colors.blue,
                          Low: BRAND.colors.green,
                        };
                        return (
                          <Cell
                            key={entry.name}
                            fill={priorityColors[entry.name] || PALETTE[idx % PALETTE.length]}
                            stroke="#fff"
                            strokeWidth={2}
                          />
                        );
                      })}
                    </Pie>
                    <Tooltip
                      formatter={(v, n) => [`${v} (${pctLabel(v, totalTasks)})`, n]}
                      contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Dev-status stacked bar per module */}
              <ChartCard
                icon={Stack}
                title="Dev Pipeline"
                subtitle="Development status stacked by module"
                empty={devStackData.rows.length === 0}
              >
                <ResponsiveContainer width="100%" height={340}>
                  <BarChart
                    data={devStackData.rows}
                    margin={{ top: 10, right: 20, left: 0, bottom: 30 }}
                  >
                    <CartesianGrid vertical={false} stroke="#F1F5F9" />
                    <XAxis
                      dataKey="module"
                      tick={{ fontSize: 11, fill: "#334155" }}
                      angle={-25}
                      textAnchor="end"
                      interval={0}
                      height={50}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11, fill: "#64748B" }}
                    />
                    <Tooltip
                      cursor={{ fill: "rgba(56,126,230,0.06)" }}
                      contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                    {devStackData.keys.map((k, i) => (
                      <Bar
                        key={k}
                        dataKey={k}
                        stackId="dev"
                        fill={PALETTE[i % PALETTE.length]}
                        radius={i === devStackData.keys.length - 1 ? [6, 6, 0, 0] : [0, 0, 0, 0]}
                        barSize={28}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            {/* Completion trend — full width */}
            <ChartCard
              icon={TrendUp}
              title="Completion Trend"
              subtitle={`Cumulative completions over the last ${trend?.days || 30} days`}
              empty={!trend || trendData.length === 0}
            >
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                  <defs>
                    <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={BRAND.colors.green} stopOpacity={0.35} />
                      <stop offset="100%" stopColor={BRAND.colors.green} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#F1F5F9" />
                  <XAxis
                    dataKey="shortDate"
                    tick={{ fontSize: 11, fill: "#64748B" }}
                    interval="preserveStartEnd"
                    minTickGap={20}
                  />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                  <Tooltip
                    formatter={(v, name) => [v, name === "cumulative" ? "Cumulative" : "Completed"]}
                    labelFormatter={(l, payload) => payload?.[0]?.payload?.date || l}
                    contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="cumulative"
                    stroke={BRAND.colors.green}
                    strokeWidth={2.5}
                    fill="url(#trendFill)"
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
              {trend && trendData.every((d) => d.cumulative === 0) ? (
                <div className="text-xs text-neutral-500 mt-2 flex items-start gap-2">
                  <Warning size={14} weight="fill" className="text-neutral-300 mt-0.5 shrink-0" />
                  <span>
                    No completions recorded in this window yet. Mark tasks as
                    <span className="font-semibold"> Completed </span>
                    on the dashboard and the trend will start filling in.
                  </span>
                </div>
              ) : null}
            </ChartCard>

            {atRiskCount > 0 ? (
              <div
                className="flex items-start gap-3 border rounded-xl p-4"
                style={{ borderColor: "#FED7AA", background: "#FFF7ED" }}
              >
                <Warning size={18} weight="fill" style={{ color: BRAND.colors.orange }} className="shrink-0 mt-0.5" />
                <div>
                  <div className="text-sm font-semibold" style={{ color: BRAND.colors.ink }}>
                    {atRiskCount} task{atRiskCount === 1 ? " is" : "s are"} flagged at risk / blocked
                  </div>
                  <div className="text-xs text-neutral-600 mt-0.5">
                    Head back to the dashboard and filter by <span className="font-semibold">Overall Status</span> (At Risk, Blocked, Critical, On Hold) to review them.
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}
